import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { PortalScene } from '../components/portal';

type VerifyState = 'verifying' | 'success' | 'error';

/**
 * Magic link verification page
 */
export function VerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { verifyToken, user } = useAuth();
  const [state, setState] = useState<VerifyState>('verifying');
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get('token');

  useEffect(() => {
    // If already logged in, redirect to dashboard
    if (user) {
      navigate('/dashboard', { replace: true });
      return;
    }

    // Verify the token
    const verify = async () => {
      if (!token) {
        setState('error');
        setError('No passage seal provided');
        return;
      }

      const result = await verifyToken(token);

      if (result.success) {
        setState('success');
        // Brief delay to show success, then redirect
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1500);
      } else {
        setState('error');
        setError(result.error ?? 'Failed to verify passage seal');
      }
    };

    verify();
  }, [token, verifyToken, navigate, user]);

  return (
    <PortalScene>
      <div className="w-full max-w-sm">
        <div className="relative bg-gradient-to-b from-night/95 to-void/95 border border-stone/30 rounded-xl p-6 backdrop-blur-sm shadow-2xl">
          {state === 'verifying' && <VerifyingContent />}
          {state === 'success' && <SuccessContent />}
          {state === 'error' && <ErrorContent error={error} />}
        </div>
      </div>
    </PortalScene>
  );
}

function VerifyingContent() {
  return (
    <div className="text-center">
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 rounded-full bg-rune/10 border border-rune/30 flex items-center justify-center">
          <svg
            className="w-8 h-8 text-rune animate-spin"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="3"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      </div>
      <h2 className="font-display text-xl text-parchment mb-2">Breaking the Seal</h2>
      <p className="text-mist text-sm">The portal awakens...</p>
    </div>
  );
}

function SuccessContent() {
  return (
    <div className="text-center">
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 rounded-full bg-rune/20 border border-rune flex items-center justify-center">
          <svg
            className="w-8 h-8 text-rune"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      </div>
      <h2 className="font-display text-xl text-parchment mb-2">The Gateway Opens</h2>
      <p className="text-mist text-sm">You have been granted passage...</p>
    </div>
  );
}

function ErrorContent({ error }: { error: string | null }) {
  return (
    <div className="text-center">
      <div className="flex justify-center mb-4">
        <div className="w-16 h-16 rounded-full bg-ember/10 border border-ember/50 flex items-center justify-center">
          <svg
            className="w-8 h-8 text-ember"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </div>
      </div>
      <h2 className="font-display text-xl text-parchment mb-2">The Seal is Broken</h2>
      <p className="text-ember text-sm mb-4">
        {error ?? 'This passage seal is invalid or expired'}
      </p>
      <a
        href="/login"
        className="text-rune hover:text-rune-glow underline text-sm transition-colors"
      >
        Request a new seal
      </a>
    </div>
  );
}
